import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { T, useGT, Num, Var } from 'gt-react';
import { Badge } from '@/components/ui/badge';
import {
  CaretDownIcon,
  CaretRightIcon,
  FlowArrowIcon,
  LinkIcon,
  LinkBreakIcon,
  CalendarDotsIcon,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import type { InvoiceChangeLogEntry } from '@erp/rpc';
import type { InvoiceActivityState } from '@/components/accounts-payable/invoice-detail/useInvoiceActivity';
import { InvoiceSnoozeChangeLine } from '@/components/accounts-payable/snooze/InvoiceSnoozeChangeLine';
import { withoutInvoiceSnoozeAuditFields } from '@/components/accounts-payable/snooze/invoiceSnoozeAudit';
import { formatFieldValue } from '@/components/common/activity-timeline/formatFieldValue';
import { cn } from '@/lib/utils';
import {
  auditReason,
  describeActor,
} from '@/components/accounts-payable/invoice-detail/invoiceChangeLogActor';

const ACTION_VARIANT: Record<
  InvoiceChangeLogEntry['action'],
  'default' | 'secondary' | 'destructive'
> = {
  INSERT: 'default',
  UPDATE: 'secondary',
  DELETE: 'destructive',
};

const ACTION_LABEL: Record<InvoiceChangeLogEntry['action'], string> = {
  INSERT: 'Created',
  UPDATE: 'Updated',
  DELETE: 'Deleted',
};

// Note: ACTION_LABEL strings will be translated at render time with useGT() in the component

const MAX_FIELD_PREVIEW = 3;

// Human-readable label for the entity a line item was matched to. Falls back
// to a title-cased version of the raw SystemEntity for unmapped types.
const MATCH_TARGET_LABELS: Record<string, string> = {
  INTERNAL_PURCHASE_ORDER_LINE_ITEM: 'purchase order line',
  AR_SALES_ORDER_LINE_ITEM: 'sales order line',
  CHARGE: 'charge',
  CORE_DOCUMENT: 'supporting document',
};

function matchTargetLabel(targetType: string | undefined): string {
  if (!targetType) return 'item';
  return (
    MATCH_TARGET_LABELS[targetType] ??
    targetType.toLowerCase().replace(/_/g, ' ')
  );
}

// Map a terminal/in-flight workflow run status to a badge variant.
function runStatusVariant(
  status: string | undefined,
): 'default' | 'secondary' | 'destructive' {
  if (status === 'failed' || status === 'terminated') return 'destructive';
  if (status === 'completed') return 'default';
  return 'secondary';
}

function getFieldNames(entry: InvoiceChangeLogEntry): string[] {
  return Object.keys(entry.changedFields ?? {});
}

function renderFieldDiff(entry: InvoiceChangeLogEntry, fieldName: string) {
  const raw = (entry.changedFields ?? {})[fieldName];
  if (entry.action === 'UPDATE' && raw && typeof raw === 'object') {
    const diff = raw as { old?: unknown; new?: unknown };
    return (
      <div className="flex items-start gap-2 text-xs font-mono">
        <span className="text-muted-foreground">{fieldName}:</span>
        <span className="text-red-600 break-all">
          {formatFieldValue(diff.old)}
        </span>
        <span className="text-muted-foreground">→</span>
        <span className="text-green-600 break-all">
          {formatFieldValue(diff.new)}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 text-xs font-mono">
      <span className="text-muted-foreground">{fieldName}:</span>
      <span className="break-all">{formatFieldValue(raw)}</span>
    </div>
  );
}

// Shared timeline shell: the left rule + dot. `accent` recolors the dot so
// match / workflow / scheduled events are scannable at a glance.
function TimelineRow({
  accent,
  children,
}: {
  accent?: string;
  children: ReactNode;
}) {
  return (
    <div className="relative pl-5 pb-3 border-l border-border last:border-l-transparent last:pb-0">
      <span
        className={cn(
          'absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full',
          accent ?? 'bg-primary',
        )}
      />
      {children}
    </div>
  );
}

/**
 * "by <actor>" for a change-log row. A change attributed to a workflow run
 * deep-links to that run so "who approved this?" is one click away.
 */
function ActorLabel({ entry }: { entry: InvoiceChangeLogEntry }) {
  const label = describeActor(entry.actor, entry.workflowName);
  if (entry.workflowId && entry.workflowRunId) {
    return (
      <Link
        to="/workflows/$workflowId/runs/$workflowRunId"
        params={{
          workflowId: entry.workflowId,
          workflowRunId: entry.workflowRunId,
        }}
        className="text-xs font-medium text-sky-700 hover:underline"
        onClick={(event) => event.stopPropagation()}
      >
        {label}
      </Link>
    );
  }
  return <span className="text-xs font-medium">{label}</span>;
}

function MatchEntryRow({ entry }: { entry: InvoiceChangeLogEntry }) {
  const timestamp = format(new Date(entry.createdAt), 'MMM d, yyyy h:mm a');
  const targetLabel = matchTargetLabel(entry.matchTargetType);
  const isUnmatch = entry.action === 'DELETE';

  return (
    <TimelineRow accent={isUnmatch ? 'bg-amber-500' : 'bg-emerald-500'}>
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">{timestamp}</span>
          {isUnmatch ? (
            <LinkBreakIcon className="h-3.5 w-3.5 text-amber-600" />
          ) : (
            <LinkIcon className="h-3.5 w-3.5 text-emerald-600" />
          )}
          <span className="text-xs font-medium">
            {isUnmatch ? (
              <T>Match removed</T>
            ) : entry.action === 'UPDATE' ? (
              <T>Match confirmed</T>
            ) : (
              <T>Line item matched</T>
            )}
          </span>
          {entry.matchSource && (
            <Badge
              variant={entry.matchSource === 'manual' ? 'default' : 'secondary'}
              className="text-[10px] h-4"
            >
              {entry.matchSource === 'manual' ? <T>Manual</T> : <T>Auto</T>}
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">
            <T context="connector word in changelog: 'changed by user'">by</T>
          </span>
          <ActorLabel entry={entry} />
        </div>
        {!isUnmatch && (
          <div className="text-xs text-muted-foreground">
            <T>
              Matched to <Var>{targetLabel}</Var>
            </T>
          </div>
        )}
      </div>
    </TimelineRow>
  );
}

function WorkflowRunEntryRow({ entry }: { entry: InvoiceChangeLogEntry }) {
  const timestamp = format(new Date(entry.createdAt), 'MMM d, yyyy h:mm a');
  const name = entry.workflowName ?? 'Workflow';

  const content = (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs text-muted-foreground">{timestamp}</span>
      <FlowArrowIcon className="h-3.5 w-3.5 text-sky-600" />
      <span className="text-xs font-medium">
        <T>Workflow ran:</T> {name}
      </span>
      {entry.runStatus && (
        <Badge
          variant={runStatusVariant(entry.runStatus)}
          className="text-[10px] h-4"
        >
          {entry.runStatus}
        </Badge>
      )}
    </div>
  );

  return (
    <TimelineRow accent="bg-sky-500">
      {entry.workflowId && entry.workflowRunId ? (
        <Link
          to="/workflows/$workflowId/runs/$workflowRunId"
          params={{
            workflowId: entry.workflowId,
            workflowRunId: entry.workflowRunId,
          }}
          className="block hover:opacity-80"
        >
          {content}
        </Link>
      ) : (
        content
      )}
    </TimelineRow>
  );
}

function ScheduledRunEntryRow({ entry }: { entry: InvoiceChangeLogEntry }) {
  const when = entry.scheduledFor
    ? format(new Date(entry.scheduledFor), 'MMM d, yyyy h:mm a')
    : null;

  return (
    <div className="rounded-md border border-dashed border-sky-300 bg-sky-50 dark:bg-sky-950/30 p-2 mb-3">
      <div className="flex items-center gap-2 flex-wrap">
        <CalendarDotsIcon className="h-4 w-4 text-sky-600" />
        <span className="text-xs font-medium">
          <T>Next scheduled run</T>
          {when ? `: ${when}` : ''}
        </span>
        {entry.workflowName && (
          <Badge variant="secondary" className="text-[10px] h-4">
            {entry.workflowName}
          </Badge>
        )}
      </div>
      {entry.snoozeReason && (
        <div className="text-xs text-muted-foreground mt-0.5">
          {entry.snoozeReason}
        </div>
      )}
    </div>
  );
}

/** "Reason: …" under a change, when the write recorded one. */
function ChangeReasonLine({
  actor,
}: {
  actor: InvoiceChangeLogEntry['actor'];
}) {
  const reason = auditReason(actor);
  if (!reason) return null;
  return (
    <div className="text-xs text-muted-foreground">
      <T context="AP invoice activity: why a field was changed">
        Reason: <Var>{reason}</Var>
      </T>
    </div>
  );
}

function ChangeLogEntryRow({ entry }: { entry: InvoiceChangeLogEntry }) {
  const gt = useGT();
  const [expanded, setExpanded] = useState(false);

  if (entry.kind === 'match') return <MatchEntryRow entry={entry} />;
  if (entry.kind === 'workflow_run')
    return <WorkflowRunEntryRow entry={entry} />;
  if (entry.kind === 'scheduled_run')
    return <ScheduledRunEntryRow entry={entry} />;

  const fieldNames = withoutInvoiceSnoozeAuditFields(getFieldNames(entry));
  const preview = fieldNames.slice(0, MAX_FIELD_PREVIEW);
  const extra = Math.max(0, fieldNames.length - MAX_FIELD_PREVIEW);
  const isLineItem = entry.entityType === 'ap_invoice_line_items';
  const timestamp = format(new Date(entry.createdAt), 'MMM d, yyyy h:mm a');
  const canExpand = fieldNames.length > 0;
  const toggle = () => setExpanded((v) => !v);

  return (
    <div className="relative pl-5 pb-3 border-l border-border last:border-l-transparent last:pb-0">
      <span className="absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full bg-primary" />
      {/* A div acting as the expand toggle (not a <button>) so the actor can be
          a real link inside it without nesting interactive elements. */}
      <div
        role={canExpand ? 'button' : undefined}
        tabIndex={canExpand ? 0 : undefined}
        aria-expanded={canExpand ? expanded : undefined}
        onClick={canExpand ? toggle : undefined}
        onKeyDown={
          canExpand
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggle();
                }
              }
            : undefined
        }
        className={cn(
          'w-full text-left flex flex-col gap-0.5',
          canExpand && 'cursor-pointer hover:opacity-80',
        )}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">{timestamp}</span>
          {isLineItem && (
            <Badge variant="outline" className="text-[10px] h-4 px-1">
              <T>Line item</T>
            </Badge>
          )}
          <Badge
            variant={ACTION_VARIANT[entry.action]}
            className="text-[10px] h-4"
          >
            {ACTION_LABEL[entry.action]}
          </Badge>
          <span className="text-xs text-muted-foreground">
            <T context="connector word in changelog: 'changed by user'">by</T>
          </span>
          <ActorLabel entry={entry} />
          {canExpand && (
            <span className="ml-auto text-muted-foreground">
              {expanded ? (
                <CaretDownIcon className="h-3 w-3" />
              ) : (
                <CaretRightIcon className="h-3 w-3" />
              )}
            </span>
          )}
        </div>
        <InvoiceSnoozeChangeLine changedFields={entry.changedFields} />
        <ChangeReasonLine actor={entry.actor} />
        {fieldNames.length > 0 && (
          <div className="text-xs text-muted-foreground">
            {entry.action === 'INSERT' || entry.action === 'DELETE' ? (
              entry.action === 'INSERT' ? (
                <T>
                  (<Num>{fieldNames.length}</Num> fields)
                </T>
              ) : (
                <T>
                  (<Num>{fieldNames.length}</Num> fields deleted)
                </T>
              )
            ) : (
              <>
                <T>Fields:</T> {preview.join(', ')}
                {extra > 0 ? ` (+${extra} ${gt('more')})` : ''}
              </>
            )}
          </div>
        )}
      </div>
      {expanded && fieldNames.length > 0 && (
        <div className="mt-2 space-y-1 rounded bg-muted/40 p-2">
          {fieldNames.map((field) => (
            <div key={field}>{renderFieldDiff(entry, field)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/** The activity list body, for hosting inside a tab or card. */
export function InvoiceActivityList({
  entries,
  isLoading,
  error,
}: InvoiceActivityState) {
  if (isLoading) {
    return (
      <div className="text-xs text-muted-foreground py-2">
        <T context="loading state for an AP invoice's activity list">
          Loading activity…
        </T>
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-xs text-red-600 py-2">
        <T context="error state for an AP invoice's activity list">
          Failed to load activity.
        </T>
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div className="text-xs text-muted-foreground py-2">
        <T context="empty state for an AP invoice's activity list">
          No activity recorded yet.
        </T>
      </div>
    );
  }
  return (
    <div>
      {entries.map((entry) => (
        <ChangeLogEntryRow key={entry.id} entry={entry} />
      ))}
    </div>
  );
}
